import React, { useState, useEffect } from "react";
import { teacherChangeList, triggerTeacher, studentTeacherChange } from '../../../Api/apiUtils';
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDate } from "../../../utils/formatDate";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import { hasEmailAccess, useEmailAccessConfig } from "../../../utils/emailAccessConfig";
import ClickToCall from "../../../Telephony/ClickToCall";
import { Download } from "react-feather";
import { Page, PageHeader, Button, Table, Loader, EmptyState, Dialog, Field, TextArea, Badge } from "../../../ui";
import "./teacherChange.scss";


// Define interfaces
interface Teacher {
  id: string;
  teacherId: string;
  teacherName: string;
  workingStatus: string;
  
lastWorkingDate?: string;
  totalStudents: number;
  reassigned: number;
  unassigned: number;
  flowTriggered: boolean;
  teacherCredentialId:any
}

interface Student {
  emailId: string;
  studentCredentialId:any;
  studentId:any;
  studentName: string;
  courses?: string[];
  phoneNumber?: string;
  expiryDate:any;
}

const escapeCsvCell = (value: unknown): string => {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
};

const TeacherChange = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [unassignedStudents, setUnassignedStudents] = useState<Student[]>([]);
  const [confirmingTeacher, setConfirmingTeacher] = useState<string | null>(null);
  const [reasonDescription, setReasonDescription] = useState<string>("");

  useEmailAccessConfig();
  const isCurrentUserRestricted = hasEmailAccess("teacherChange");

  useEffect(() => {
    fetchTeachers();
  }, []);

  // Fetch teachers from the API
  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response: Teacher[] = await teacherChangeList();
      setTeachers(response.map(teacher => ({
        ...teacher,
        flowTriggered: false
      })));
    } catch (error) {
      console.error("Error fetching teachers:", error);
    } finally {
      setLoading(false);
    }
  };

 

  // Trigger teacher change
  const triggerChangeTeacher = async (teacherId: string, reasonDescriptionValue: string) => {
    const selectedTeacherDetails = teachers.find((teacher) => teacher.id === teacherId || teacher.teacherId === teacherId);
    try {
      setModalLoading(true);
      await triggerTeacher(teacherId, { reasonDescription: reasonDescriptionValue }); // Use the API to trigger teacher change
      trackEvent(MixpanelEvents.TEACHER_CHANGE_DETACHMENT, {
        action: "trigger_teacher_change_success",
        teacherId,
        teacherName: selectedTeacherDetails?.teacherName || "",
        totalStudents: selectedTeacherDetails?.totalStudents || 0,
        reasonDescription: reasonDescriptionValue,
      });
      setTeachers(prevTeachers =>
        prevTeachers.map(teacher =>
          teacher.id === teacherId ? { ...teacher, flowTriggered: true } : teacher
        )
      );
      toast.warning("Teacher detachment is in progress, Please refresh after some time!")
      fetchTeachers(); // Refresh the list
    } catch (error) {
      console.error("Error triggering teacher change:", error);
      trackEvent(MixpanelEvents.TEACHER_CHANGE, {
        action: "trigger_teacher_change_failed",
        teacherId,
        teacherName: selectedTeacherDetails?.teacherName || "",
        reasonDescription: reasonDescriptionValue,
      });
    } finally {
      setModalLoading(false);
      setConfirmingTeacher(null);
    }
  };

  // Download CSV
  const downloadCSV = () => {
    const csvRows = [
      ["Teacher Name", "Status", "Date of Departure", "Total Students", "Reassigned", "Unassigned"],
      ...teachers.map(teacher => [
        teacher.teacherName,
        teacher.workingStatus,
        teacher.
        lastWorkingDate ? new Date(teacher.
          lastWorkingDate).toLocaleDateString() : "N/A",
        teacher.totalStudents,
        teacher.reassigned,
        teacher.unassigned
      ])
    ];
    const csvContent = csvRows.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "teachers.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Fetch unassigned students for a teacher
  const openUnassignedModal = async (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setModalLoading(true);
    try {
      const response: Student[] = await studentTeacherChange("unassigned", teacher.teacherId);
      setUnassignedStudents(response);
    } catch (error) {
      console.error("Error fetching unassigned students:", error);
      setUnassignedStudents([]); // Reset unassigned students on error
    } finally {
      setModalLoading(false);
    }
  };

  // Close modals
  const closeModal = () => {
    setSelectedTeacher(null);
    setConfirmingTeacher(null);
    setUnassignedStudents([]);
    setReasonDescription("");
  };

  const downloadUnassignedStudentsCSV = () => {
    if (!selectedTeacher || unassignedStudents.length === 0) {
      toast.warning("No unassigned students to download.");
      return;
    }
    const teacherName = selectedTeacher.teacherName || "";
    const headers = ["Teacher Name", "Name", "Courses", "Email", "Phone", "Expiry Date"];
    const csvRows = [
      headers.map(escapeCsvCell).join(","),
      ...unassignedStudents.map((student) =>
        [
          teacherName,
          student.studentName || "",
          student.courses ? student.courses.join(", ") : "",
          student.emailId || "",
          student.phoneNumber || "",
          student.expiryDate ? formatDate(student.expiryDate, "dateTime") : "",
        ]
          .map(escapeCsvCell)
          .join(","),
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `unassigned_students_${teacherName.replace(/\s+/g, "_")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const isExpired = (date?: string) => !!date && new Date(date) < new Date();

  return (
    <Page>
      <PageHeader
        eyebrow="Consumer Xperience"
        title="Teacher Change"
        subtitle={loading ? "Loading teachers…" : `${teachers.length} departing teachers`}
        actions={
          <Button variant="secondary" icon={<Download size={16} aria-hidden />} onClick={downloadCSV}>
            Download CSV
          </Button>
        }
      />

      {loading ? (
        <Loader label="Loading teachers…" />
      ) : teachers.length === 0 ? (
        <EmptyState title="No departing teachers" description="Teachers marked as leaving will show up here." />
      ) : (
        <Table caption="Departing teachers" stickyHeader wrapClassName="teacher-change-scroll">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Status</th>
              <th>Date of departure</th>
              <th className="ds-num">Total students</th>
              <th>Detach students</th>
              <th className="ds-num">Reassigned</th>
              <th className="ds-num">Unassigned</th>
            </tr>
          </thead>
          <tbody>
            {teachers.map(teacher => (
              <tr key={teacher.id}>
                <td>
                  <Link className="ds-link" to={`/pages/teacher-profile/${teacher.teacherCredentialId}/${teacher.teacherId}`}>
                    {teacher.teacherName}
                  </Link>
                </td>
                <td><Badge>{teacher.workingStatus}</Badge></td>
                <td>{teacher.lastWorkingDate ? formatDate(teacher.lastWorkingDate,"shortDate") : "N/A"}</td>
                <td className="ds-num">{teacher.totalStudents || 0}</td>
                <td>
                  {teacher.totalStudents > 0 && (
                    teacher.flowTriggered ? (
                      <Badge tone="info">Flow triggered</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setConfirmingTeacher(teacher.teacherId)}
                        disabled={!isCurrentUserRestricted}
                      >
                        Detach students
                      </Button>
                    )
                  )}
                </td>
                <td className="ds-num">{teacher.reassigned}</td>
                <td className="ds-num">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openUnassignedModal(teacher)}
                    aria-label={`Show ${teacher.unassigned} unassigned students for ${teacher.teacherName}`}
                  >
                    {teacher.unassigned}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Dialog
        open={!!confirmingTeacher}
        onClose={closeModal}
        title="Detach students from this teacher?"
        actions={
          <>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => confirmingTeacher && triggerChangeTeacher(confirmingTeacher, reasonDescription)}
              disabled={!reasonDescription.trim()}
              loading={modalLoading}
            >
              Detach students
            </Button>
          </>
        }
      >
        <p>This unlinks every student assigned to this teacher.</p>
        <Field label="Reason" required>
          {(ctl) => (
            <TextArea
              {...ctl}
              value={reasonDescription}
              onChange={(e) => setReasonDescription(e.target.value)}
              placeholder="Why are the students being detached?"
            />
          )}
        </Field>
      </Dialog>

      <Dialog
        open={!!selectedTeacher}
        onClose={closeModal}
        size="xl"
        title={`Unassigned students · ${selectedTeacher?.teacherName ?? ""}`}
        actions={
          <>
            <Button variant="secondary" onClick={closeModal}>Close</Button>
            <Button
              variant="primary"
              icon={<Download size={16} aria-hidden />}
              onClick={downloadUnassignedStudentsCSV}
              disabled={modalLoading || unassignedStudents.length === 0}
            >
              Download CSV
            </Button>
          </>
        }
      >
        {modalLoading ? (
          <Loader label="Loading students…" />
        ) : unassignedStudents.length > 0 ? (
          <Table caption="Unassigned students" stickyHeader wrapClassName="teacher-change-modal-scroll">
            <thead>
              <tr>
                <th>Name</th>
                <th>Courses</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Expiry date</th>
              </tr>
            </thead>
            <tbody>
              {unassignedStudents.map((student) => (
                <tr key={student.studentId || student.emailId || student.studentName}>
                  <td>
                    <Link className="ds-link" to={`/pages/student-profile/${student.studentCredentialId}/${student.studentId}`}>
                      {student.studentName || "-"}
                    </Link>
                  </td>
                  <td>{student.courses ? student.courses.join(", ") : "-"}</td>
                  <td>{student.emailId || "-"}</td>
                  <td><ClickToCall number={student.phoneNumber || undefined} name={student.studentName} sourceRecordId={student.studentId} /></td>
                  <td>
                    {formatDate(student?.expiryDate, "dateTime") || "_"}{" "}
                    {isExpired(student.expiryDate) && <Badge tone="danger">Expired</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState title="No unassigned students" description="Every student of this teacher has been reassigned." />
        )}
      </Dialog>
    </Page>
  );
};

export default TeacherChange;